import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { MAX_RESUME_SIZE, ACCEPTED_RESUME_TYPES, ACCEPTED_RESUME_EXTENSIONS } from "@/modules/candidate/constants/candidateApplication.constants";

export function useResumeUpload() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedResume, setSelectedResume] = useState<File | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateResume = (file: File) => {
    const normalizedFileName = file.name.toLowerCase();
    const hasValidExtension = ACCEPTED_RESUME_EXTENSIONS.some((ext) => normalizedFileName.endsWith(ext));
    const hasValidMimeType = ACCEPTED_RESUME_TYPES.includes(file.type);

    if (!hasValidExtension && !hasValidMimeType) {
      return "Please upload a PDF, DOC, or DOCX resume.";
    }
    if (file.size > MAX_RESUME_SIZE) {
      return "Resume size must be 5 MB or less.";
    }
    return null;
  };

  const handleResumeSelection = (file: File) => {
    setResumeError(null);
    const validationError = validateResume(file);

    if (validationError) {
      setSelectedResume(null);
      setResumeError(validationError);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setSelectedResume(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleResumeSelection(file);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    handleResumeSelection(file);
  };

  const handleRemoveResume = () => {
    setSelectedResume(null);
    setResumeError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const reset = () => {
    setSelectedResume(null);
    setResumeError(null);
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return {
    fileInputRef,
    selectedResume,
    resumeError,
    isDragging,
    setResumeError,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveResume,
    reset,
  };
}